import React, { forwardRef } from 'react';
import Spinner from './Spinner';

const cx = (...names) => names.filter(Boolean).join(' ');

/**
 * Button: primary (one per area), secondary, ghost, danger. `icon` is an
 * icon component drawn before the label; `loading` keeps the width, shows a
 * spinner and blocks clicks. `as` renders another element (a router Link).
 */
export const Button = forwardRef(function Button(
  { variant = 'primary', size = 'md', icon: Icon, iconRight: IconRight, loading = false, block = false,
    as: Comp = 'button', type, className, disabled, children, ...rest },
  ref,
) {
  const isButton = Comp === 'button';
  return (
    <Comp
      ref={ref}
      type={isButton ? (type || 'button') : type}
      className={cx('ui-btn', `ui-btn--${variant}`, `ui-btn--${size}`, block && 'ui-btn--block', loading && 'is-loading', className)}
      disabled={isButton ? (disabled || loading) : undefined}
      aria-disabled={!isButton && (disabled || loading) ? true : undefined}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={16} className="ui-btn__spinner" /> : Icon && <Icon size={size === 'sm' ? 16 : 18} />}
      {children != null && <span className="ui-btn__label">{children}</span>}
      {IconRight && !loading && <IconRight size={size === 'sm' ? 16 : 18} />}
    </Comp>
  );
});

/** A button with only an icon: `label` is required and becomes its accessible name. */
export const IconButton = forwardRef(function IconButton(
  { icon: Icon, label, variant = 'secondary', size = 'md', className, as: Comp = 'button', type, iconSize, ...rest },
  ref,
) {
  if (process.env.NODE_ENV !== 'production' && !label) {
    // eslint-disable-next-line no-console
    console.error('IconButton needs a label (its accessible name).');
  }
  const isButton = Comp === 'button';
  return (
    <Comp
      ref={ref}
      type={isButton ? (type || 'button') : type}
      aria-label={label}
      className={cx('ui-icon-btn', `ui-icon-btn--${variant}`, `ui-icon-btn--${size}`, className)}
      {...rest}
    >
      {Icon && <Icon size={iconSize || (size === 'sm' ? 16 : 18)} />}
    </Comp>
  );
});

export default Button;
